import { useState } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { useAuthContext } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Header } from '@/components/ui/header'
import { Mail, Lock, Loader2, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'

type LoginMode = 'password' | 'magic-link'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { signInWithPassword, signInWithMagicLink } = useAuthContext()

  const [mode, setMode] = useState<LoginMode>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return

    setIsLoading(true)
    const { error } = await signInWithPassword(email, password)
    setIsLoading(false)

    if (error) {
      if (error.message.includes('Email not confirmed')) {
        toast.error(t('auth.toastConfirmEmailFirst'))
        navigate('/confirm-email', { state: { email } })
      } else {
        toast.error(error.message || t('auth.toastSignInFailed'))
      }
      return
    }

    toast.success(t('auth.toastWelcomeBack'))
    navigate(from, { replace: true })
  }

  const handleMagicLinkLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsLoading(true)
    const { error } = await signInWithMagicLink(email)
    setIsLoading(false)

    if (error) {
      toast.error(error.message || t('auth.toastMagicLinkFailed'))
      return
    }

    setMagicLinkSent(true)
    toast.success(t('auth.toastMagicLinkSent'))
  }

  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-warm-gray flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
              <Mail className="w-8 h-8 text-accent" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-gray-900">{t('auth.checkYourEmail')}</h2>
              <p className="text-gray-500">
                <Trans
                  i18nKey="auth.magicLinkSentTo"
                  values={{ email }}
                  components={{ strong: <span className="font-medium text-gray-900" /> }}
                />
              </p>
            </div>
            <p className="text-sm text-gray-400">{t('auth.magicLinkExpiry')}</p>
            <Button variant="outline" onClick={() => setMagicLinkSent(false)} className="mt-4">
              {t('auth.useDifferentEmail')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-gray flex flex-col">
      <Header />

      <div className="flex-1 flex">
        {/* Left Panel - Branding (desktop only) */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between items-start">
          <div />
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight text-balance">
              {t('auth.loginHeroTitle')}
            </h1>
            <p className="text-lg text-white/80 max-w-md">{t('auth.loginHeroSubtitle')}</p>
          </div>
          <div />
        </div>

        {/* Right Panel - Login Form */}
        <div className="flex-1 flex flex-col justify-center p-8 lg:p-12">
          <div className="max-w-md mx-auto w-full bg-white rounded-2xl border border-gray-200 p-8 shadow-sm space-y-8">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-gray-900">{t('auth.signIn')}</h2>
              <p className="text-gray-500">{t('auth.signInSubtitle')}</p>
            </div>

            {/* Login Mode Tabs */}
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                onClick={() => setMode('password')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  mode === 'password'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('auth.tabPassword')}
              </button>
              <button
                onClick={() => setMode('magic-link')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  mode === 'magic-link'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('auth.tabMagicLink')}
              </button>
            </div>

            <form
              onSubmit={mode === 'password' ? handlePasswordLogin : handleMagicLinkLogin}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.emailLabel')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {mode === 'password' && (
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder={t('auth.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full h-12 text-base group"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {mode === 'password' ? t('auth.signIn') : t('auth.sendMagicLink')}
                    <ChevronRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center space-y-4">
              <p className="text-sm text-gray-500">
                {t('auth.noAccount')}{' '}
                <Link to="/signup" className="font-medium text-accent hover:underline">
                  {t('auth.signUp')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
